import { Contact, ContactInput, ContactsResponse, Category, CategoryInput, CategoriesResponse, ImportResult, ImportOptions, DryRunResult, PerfexLead } from '../types';

const API_BASE_URL = '/api';

interface QuotaError extends Error {
  isQuotaError?: boolean;
  upgradeRequired?: boolean;
}

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('auth_token');
    let tenantId = localStorage.getItem('selected_tenant_id');

    // Se não houver tenantId no localStorage, tentar recuperar do token decodificado ou do estado da aplicação
    // Isso evita o erro MISSING X-Tenant-Id durante o boot inicial
    if (!tenantId && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.tenantId) {
          tenantId = payload.tenantId;
        }
      } catch (e) {
        // Silently fail decoding
      }
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (tenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      cache: 'no-store',
    });

    const debugTenant = response.headers.get('X-Debug-Tenant-Id');
    const debugRole = response.headers.get('X-Debug-User-Role');
    if (debugTenant) {
      try { sessionStorage.setItem('debug_tenant_id', debugTenant); } catch (err) { /* ignore */ }
    }
    if (debugRole) {
      try { sessionStorage.setItem('debug_user_role', debugRole); } catch (err) { /* ignore */ }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

      // Detectar erro de quota e adicionar flag para tratamento especial
      if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
        const quotaError = new Error(error.message || error.error || 'Limite atingido') as QuotaError;
        quotaError.isQuotaError = true;
        quotaError.upgradeRequired = true;
        throw quotaError;
      }

      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  async getContacts(params?: {
    search?: string;
    tag?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ContactsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<ContactsResponse>(endpoint);
  }

  async getContact(id: string): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`);
  }

  async createContact(data: ContactInput): Promise<Contact> {
    return this.request<Contact>('/contatos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string): Promise<void> {
    await this.request<void>(`/contatos/${id}`, {
      method: 'DELETE',
    });
  }

  async deleteAllContacts(): Promise<{ deleted: number }> {
    return this.request<{ deleted: number }>('/contatos/all', {
      method: 'DELETE',
    });
  }

  // Category methods
  async getCategories(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CategoriesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/categorias${queryString ? `?${queryString}` : ''}`;

    return this.request<CategoriesResponse>(endpoint);
  }

  async getAllCategories(): Promise<Category[]> {
    return this.request<Category[]>('/categorias/all');
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<Category> {
    return this.request<Category>('/categorias', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: CategoryInput): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request<void>(`/categorias/${id}`, {
      method: 'DELETE',
    });
  }

  // CSV Import methods
  async importCSV(file: File, options?: ImportOptions): Promise<ImportResult | DryRunResult> {
    const formData = new FormData();
    formData.append('csv', file);

    if (options) {
      formData.append('options', JSON.stringify(options));
    }

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  // Bulk operations
  async post<T = unknown>(endpoint: string, data: unknown): Promise<T> {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Chatwoot integration
  async getChatwootTags(): Promise<{ tags: Array<{ name: string; count: number }> }> {
    return this.request('/chatwoot/tags');
  }

  async syncChatwootContacts(tagMappings: Array<{ chatwootTag: string; categoryId: string }>): Promise<{ imported: number; updated: number }> {
    return this.request('/chatwoot/sync', {
      method: 'POST',
      body: JSON.stringify({ tagMappings }),
    });
  }

  // Perfex CRM Integration
  async getPerfexLeads(): Promise<{ success: boolean; leads: PerfexLead[]; total: number }> {
    return this.request('/perfex/leads');
  }

  async importPerfexLeads(leadIds: string[], categoryId: string): Promise<{ success: boolean; imported: number; updated: number; errors: number }> {
    return this.request('/perfex/import', {
      method: 'POST',
      body: JSON.stringify({ leadIds, categoryId }),
    });
  }

  async getPerfexStatuses(): Promise<{ success: boolean; statuses: Array<{ id: string; name: string; color?: string }> }> {
    return this.request('/perfex/statuses');
  }

  async getPerfexSources(): Promise<{ success: boolean; sources: Array<{ id: string; name: string }> }> {
    return this.request('/perfex/sources');
  }

  async getPerfexStaff(): Promise<{ success: boolean; staff: Array<{ staffid: string; firstname: string; lastname: string; email: string }> }> {
    return this.request('/perfex/staff');
  }

  // Dashboard methods
  async getDashboardStats(): Promise<{ contacts: number; messagesSent: number; connections: number }> {
    return this.request('/dashboard/stats');
  }

  async getMessagesPerDay(): Promise<Array<{ date: string; count: number }>> {
    return this.request('/dashboard/messages-per-day');
  }

  async getRecentCampaigns(): Promise<Array<{
    id: string;
    nome: string;
    status: string;
    criadoEm: string;
    totalContacts: number;
    sentCount: number;
    failedCount: number;
    startedAt: string | null;
    completedAt: string | null;
  }>> {
    return this.request('/dashboard/recent-campaigns');
  }

  async updateProfile(nome: string): Promise<{ success: boolean; message: string; data: { user: { id: string; nome: string; email: string; role: string; ativo: boolean; avatarUrl?: string } } }> {
    return this.request('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify({ nome }),
    });
  }

  async updateAvatar(avatarUrl: string): Promise<{ success: boolean; message: string; data: { user: { id: string; nome: string; email: string; role: string; ativo: boolean; avatarUrl?: string } } }> {
    return this.request('/auth/me/avatar', {
      method: 'POST',
      body: JSON.stringify({ avatarUrl }),
    });
  }

  async deleteAvatar(): Promise<{ success: boolean; message: string; data: { user: { id: string; nome: string; email: string; role: string; ativo: boolean; avatarUrl?: string } } }> {
    return this.request('/auth/me/avatar', {
      method: 'DELETE',
    });
  }

  async updatePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> {
    return this.request('/auth/me/password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    });
  }
}

export const apiService = new ApiService();
export const api = apiService;
